import { useState, useEffect } from 'react';
import type { DatabaseState } from '@/types/database';

// Helper function to fetch with better error handling
async function fetchJSON(url: string, name: string) {
  try {
    console.log(`[useDatabase] Fetching ${name} from ${url}`);
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch ${name}: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`[useDatabase] Successfully loaded ${name}:`, Array.isArray(data) ? `${data.length} items` : 'object');
    return data;
  } catch (err) {
    console.error(`[useDatabase] Error loading ${name}:`, err);
    throw new Error(`Failed to load ${name}: ${err instanceof Error ? err.message : String(err)}`);
  }
}

export function useDatabase() {
  const [database, setDatabase] = useState<DatabaseState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadDatabase() {
      console.log('[useDatabase] Starting database load...');
      try {
        const [
          studies,
          mushrooms,
          compounds,
          conditions,
          interventions,
          outcomes,
          study_compounds,
          study_conditions,
          safety_data,
          marketing_angles,
          study_connections
        ] = await Promise.all([
          fetchJSON('/data/studies.json', 'studies'),
          fetchJSON('/data/mushrooms.json', 'mushrooms'),
          fetchJSON('/data/compounds.json', 'compounds'),
          fetchJSON('/data/conditions.json', 'conditions'),
          fetchJSON('/data/interventions.json', 'interventions'),
          fetchJSON('/data/outcomes.json', 'outcomes'),
          fetchJSON('/data/study_compounds.json', 'study_compounds'),
          fetchJSON('/data/study_conditions.json', 'study_conditions'),
          fetchJSON('/data/safety_data.json', 'safety_data'),
          fetchJSON('/data/marketing_angles.json', 'marketing_angles'),
          fetchJSON('/data/study_connections.json', 'study_connections')
        ]);

        const dbState = {
          studies,
          mushrooms,
          compounds,
          conditions,
          interventions,
          outcomes,
          study_compounds,
          study_conditions,
          safety_data,
          marketing_angles,
          study_connections
        };

        console.log('[useDatabase] All data loaded successfully:', {
          studies: studies?.length,
          mushrooms: mushrooms?.length,
          compounds: compounds?.length,
          conditions: conditions?.length
        });

        setDatabase(dbState);
        setLoading(false);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load database';
        console.error('[useDatabase] Database load failed:', errorMessage, err);
        setError(errorMessage);
        setLoading(false);
      }
    }

    loadDatabase();
  }, []);

  return { database, loading, error };
}
